/**
 * Adding, renaming, recolouring, reordering and retiring the product
 * categories.
 *
 * These rows were editable in the database all along — the server has had
 * create, rename, recolour and delete since the beginning — but nothing in the
 * app ever called them, so the shop was stuck with whatever list it started
 * with. This is that missing screen.
 *
 * Reached from Products & recipes, beside the products they group, for the
 * same reason the stock room's version sits beside the ingredients: you notice
 * the grouping is wrong while you are looking at it, not while you are in
 * Settings.
 *
 * Two things here matter more than they do in the stock room. The colour is
 * what tints the chips along the top of the till, so it is a working signal
 * rather than decoration. And the order is the order those chips appear in, so
 * the things rung up all day belong at the front.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Pencil, Plus, Trash2 } from 'lucide-react';

import { api } from '../lib/api.js';
import type { Category } from '@bakery/shared';
import { Button, Input, Modal, Skeleton, cx, useToast } from '../components/ui.js';

export function ProductCategoriesModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const categories = useQuery({ queryKey: ['categories', 'all'], queryFn: api.catalog.allCategories });
  const products = useQuery({ queryKey: ['products'], queryFn: () => api.catalog.products() });

  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  /** Which category is one more click from being deleted. */
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  /** How many products each category holds, so the cost of removing one shows. */
  const counts = useMemo(() => {
    const tally = new Map<string, number>();
    for (const product of products.data ?? []) {
      if (product.categoryId) tally.set(product.categoryId, (tally.get(product.categoryId) ?? 0) + 1);
    }
    return tally;
  }, [products.data]);

  const ordered = categories.data ?? [];

  const refresh = () => {
    // Both the picker in the product form and the chips at the till read these.
    queryClient.invalidateQueries({ queryKey: ['categories'] });
    queryClient.invalidateQueries({ queryKey: ['products'] });
  };

  const create = useMutation({
    // Added at the end rather than the front: a new category has not earned a
    // place in front of the ones being rung up all day.
    mutationFn: () =>
      api.catalog.createCategory({
        name: newName.trim(),
        sortOrder: ordered.reduce((max, category) => Math.max(max, category.sortOrder), -1) + 1,
      }),
    onSuccess: (created) => {
      setNewName('');
      refresh();
      toast.push({ tone: 'ok', title: `${created.name} added` });
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not add it', body: error.message }),
  });

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => api.catalog.updateCategory(id, { name }),
    onSuccess: () => {
      setEditingId(null);
      refresh();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not rename it', body: error.message }),
  });

  const recolour = useMutation({
    mutationFn: ({ id, color }: { id: string; color: string }) => api.catalog.updateCategory(id, { color }),
    onSuccess: refresh,
  });

  const setActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.catalog.updateCategory(id, { isActive }),
    onSuccess: refresh,
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not change it', body: error.message }),
  });

  /**
   * Move one category past its neighbour.
   *
   * Renumbers from the list as shown rather than swapping the two rows' stored
   * positions, because those positions are not guaranteed to differ — the API
   * has always defaulted a new category's to 0, so a shop can easily hold
   * several at 0 that the server then breaks ties on by name. Swapping two
   * equal numbers moves nothing and the button would look broken. Writing
   * positions back from the visible order fixes the ties on the way past.
   *
   * Only the rows whose number actually changes are sent.
   */
  const move = useMutation({
    mutationFn: async ({ category, by }: { category: Category; by: -1 | 1 }) => {
      const index = ordered.findIndex((c) => c.id === category.id);
      const target = index + by;
      if (target < 0 || target >= ordered.length) return;

      const next = [...ordered];
      [next[index], next[target]] = [next[target]!, next[index]!];

      for (const [position, row] of next.entries()) {
        if (row.sortOrder !== position) await api.catalog.updateCategory(row.id, { sortOrder: position });
      }
    },
    onSuccess: refresh,
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not move it', body: error.message }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.catalog.deleteCategory(id),
    onSuccess: (_result, id) => {
      const stranded = counts.get(id) ?? 0;
      setConfirmingId(null);
      refresh();
      toast.push({
        tone: 'ok',
        title: 'Category deleted',
        body:
          stranded > 0
            ? `${stranded} product${stranded === 1 ? '' : 's'} kept, now with no category. Open one to file it again.`
            : undefined,
      });
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not delete it', body: error.message }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Product categories"
      description="How the till and this list are grouped. The colour and order here are the chips along the top of the Register."
      footer={
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="space-y-3">
        <div className="flex gap-2">
          <Input
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && newName.trim()) create.mutate();
            }}
            placeholder="New category, e.g. Sandwiches"
          />
          <Button
            variant="primary"
            icon={<Plus className="size-4" />}
            loading={create.isPending}
            disabled={!newName.trim()}
            onClick={() => create.mutate()}
          >
            Add
          </Button>
        </div>

        {categories.isLoading ? (
          <Skeleton className="h-48" />
        ) : (
          <ul className="divide-y divide-[var(--color-line)]">
            {ordered.map((category, index) => {
              const used = counts.get(category.id) ?? 0;
              const isEditing = editingId === category.id;
              const isConfirming = confirmingId === category.id;

              return (
                <li key={category.id} className="flex items-center gap-2 py-2">
                  <input
                    type="color"
                    value={category.color}
                    onChange={(event) => recolour.mutate({ id: category.id, color: event.target.value })}
                    title={`Colour for ${category.name}`}
                    aria-label={`Colour for ${category.name}`}
                    className="size-6 shrink-0 cursor-pointer rounded-full border border-[var(--color-line-strong)] bg-transparent p-0"
                  />

                  {isEditing ? (
                    <Input
                      autoFocus
                      value={editingName}
                      onChange={(event) => setEditingName(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && editingName.trim()) {
                          rename.mutate({ id: category.id, name: editingName.trim() });
                        }
                        if (event.key === 'Escape') setEditingId(null);
                      }}
                      className="h-9 min-w-0 flex-1"
                    />
                  ) : (
                    <div className="min-w-0 flex-1">
                      <span className={cx('text-sm font-medium', !category.isActive && 'line-through opacity-50')}>
                        {category.name}
                      </span>
                      <span className="ml-2 text-xs text-[var(--color-ink-faint)]">
                        {used === 0 ? 'nothing filed under it' : `${used} product${used === 1 ? '' : 's'}`}
                      </span>
                    </div>
                  )}

                  {isEditing ? (
                    <>
                      <Button
                        size="sm"
                        variant="primary"
                        loading={rename.isPending}
                        disabled={!editingName.trim()}
                        onClick={() => rename.mutate({ id: category.id, name: editingName.trim() })}
                      >
                        Save
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                        Cancel
                      </Button>
                    </>
                  ) : isConfirming ? (
                    /*
                      * Deleting is not undoable and quietly unfiles whatever was
                      * in it, so it asks — and says how many products that is,
                      * which is the number worth knowing before answering.
                      */
                    <>
                      <span className="text-xs text-[var(--color-ink-soft)]">
                        {used === 0 ? 'Delete it?' : `Unfile ${used} and delete?`}
                      </span>
                      <Button
                        size="sm"
                        variant="danger"
                        loading={remove.isPending}
                        onClick={() => remove.mutate(category.id)}
                      >
                        Delete
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmingId(null)}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      {/* The order of the chips at the till. */}
                      <div className="flex shrink-0 flex-col">
                        <button
                          type="button"
                          disabled={index === 0 || move.isPending}
                          onClick={() => move.mutate({ category, by: -1 })}
                          title={`Move ${category.name} earlier`}
                          aria-label={`Move ${category.name} earlier`}
                          className="rounded-[var(--radius-sm)] px-1 text-[var(--color-ink-faint)] transition hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-ink)] disabled:pointer-events-none disabled:opacity-25"
                        >
                          <ChevronUp className="size-3.5" />
                        </button>
                        <button
                          type="button"
                          disabled={index === ordered.length - 1 || move.isPending}
                          onClick={() => move.mutate({ category, by: 1 })}
                          title={`Move ${category.name} later`}
                          aria-label={`Move ${category.name} later`}
                          className="rounded-[var(--radius-sm)] px-1 text-[var(--color-ink-faint)] transition hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-ink)] disabled:pointer-events-none disabled:opacity-25"
                        >
                          <ChevronDown className="size-3.5" />
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(category.id);
                          setEditingName(category.name);
                        }}
                        title={`Rename ${category.name}`}
                        aria-label={`Rename ${category.name}`}
                        className="rounded-[var(--radius-md)] p-1.5 text-[var(--color-ink-faint)] transition hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-ink)]"
                      >
                        <Pencil className="size-4" />
                      </button>

                      {/*
                        * Hiding takes it off the till without unfiling anything,
                        * which is what a shop usually wants for something
                        * seasonal. Deleting is for a category made by mistake.
                        */}
                      <button
                        type="button"
                        onClick={() => setActive.mutate({ id: category.id, isActive: !category.isActive })}
                        className="rounded-[var(--radius-md)] px-2 py-1 text-xs text-[var(--color-ink-soft)] transition hover:bg-[var(--color-surface-sunken)]"
                      >
                        {category.isActive ? 'Hide' : 'Show'}
                      </button>

                      <button
                        type="button"
                        onClick={() => setConfirmingId(category.id)}
                        title={`Delete ${category.name}`}
                        aria-label={`Delete ${category.name}`}
                        className="rounded-[var(--radius-md)] p-1.5 text-[var(--color-ink-faint)] transition hover:bg-[var(--color-danger-soft)] hover:text-[var(--color-danger)]"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {!categories.isLoading && ordered.length === 0 && (
          <p className="py-6 text-center text-sm text-[var(--color-ink-faint)]">
            No categories yet. Add one above and it appears at the till.
          </p>
        )}
      </div>
    </Modal>
  );
}
